import type { ProfileSystemKey } from '@brushfire/types'
import type { ChoiceTextResponseConfig } from './choiceTextResponse'
import { newObjectIdHex } from './objectIdHex'

export type QuestionTypeId =
  | 'MULTIPLE_CHOICE'
  | 'YES_NO'
  | 'CHECKBOX'
  | 'DROPDOWN'
  | 'SHORT_TEXT'
  | 'LONG_TEXT'
  | 'DATE'
  | 'RATING'
  | 'LIKERT'
  | 'SLIDER'
  | 'PICTURE_CHOICE'
  | 'RANKING'
  | 'VIDEO'

export const CORE_QUESTION_TYPES: readonly QuestionTypeId[] = [
  'MULTIPLE_CHOICE',
  'PICTURE_CHOICE',
  'YES_NO',
  'CHECKBOX',
  'DROPDOWN',
  'SHORT_TEXT',
  'LONG_TEXT',
  'DATE',
  'RATING',
  'RANKING',
  'LIKERT',
  'SLIDER',
  'VIDEO',
] as const

export interface ShortTextConfig {
  /** Optional min length (positive integer; empty = omit on API). */
  minLength: string
  /** Optional max length for the answer (positive integer; empty = omit on API). */
  maxLength: string
  /** Placeholder shown in the respondent input (draft `placeholder`). */
  hintPlaceholder: string
}

export type VideoSourceKind = 'link' | 'upload'

/** Builder state for `VIDEO` — one inline source + short-text response validation. */
export interface VideoConfig extends ShortTextConfig {
  sourceKind: VideoSourceKind
  /** YouTube / Vimeo / direct MP4 URL, or uploaded serve path. */
  videoUrl: string
  /** Original filename when `sourceKind` is `upload`. */
  uploadedFileName: string
}

/** Figma `4399:9194` — empty Short Text min field hint (limit example **10**). */
export const SHORT_TEXT_MIN_LENGTH_PLACEHOLDER = 'e.g. 10' as const

/** Figma `4399:9199` — empty Short Text max field hint (limit example **150**). */
export const SHORT_TEXT_MAX_LENGTH_PLACEHOLDER = 'e.g. 150' as const

export interface MultipleChoiceConfig {
  options: string[]
  /** Stable Mongo ObjectIds aligned 1:1 with `options` by index (BFP-378). */
  optionIds?: string[]
  other: boolean
  noneOfAbove: boolean
  /** BFP-1156 — the one option (incl. Other / None) that asks respondents for text. */
  textResponse?: ChoiceTextResponseConfig | null
  minSelection: string
  maxSelection: string
  layoutHorizontal: boolean
  multiColumn: boolean
  randomize: boolean
}

export interface YesNoConfig {
  yesLabel: string
  noLabel: string
  layoutHorizontal: boolean
  toggleStyle: boolean
}

/** Fixed respondent copy when checkbox `selectAllLabel` toggle is enabled (BFP-181 / BFP-508). */
export const CHECKBOX_SELECT_ALL_CAPTION = 'Select all that apply' as const

export interface CheckboxConfig {
  options: string[]
  /** Stable Mongo ObjectIds aligned 1:1 with `options` by index (BFP-378). */
  optionIds?: string[]
  other: boolean
  noneOfAbove: boolean
  /** BFP-1156 — the one option (incl. Other / None) that asks respondents for text. */
  textResponse?: ChoiceTextResponseConfig | null
  minSelection: string
  maxSelection: string
  /** When true, show fixed {@link CHECKBOX_SELECT_ALL_CAPTION} above options in preview and on draft persist. */
  selectAllLabel: boolean
  layoutHorizontal: boolean
  multiColumn: boolean
  randomize: boolean
}

export interface DropdownConfig {
  options: string[]
  /** Stable Mongo ObjectIds aligned 1:1 with `options` by index (BFP-378). */
  optionIds?: string[]
  other: boolean
  noneOfAbove: boolean
  randomize: boolean
}

/** Respondent-facing date layout (Figma 4399:9454–9460); synced across all DATE questions in the builder. */
export type DateInputDisplayFormat = 'dd/MM/yyyy' | 'MM/dd/yyyy'

/** Builder state for `DATE` (Figma 4399:9054 — restrictions + toggles + format + placeholder). */
export interface DateConfig {
  /** Optional lower bound (`yyyy-mm-dd`). */
  earliestSelectableDate: string
  /** Optional upper bound (`yyyy-mm-dd`). */
  latestSelectableDate: string
  disablePastDates: boolean
  disableFutureDates: boolean
  /** Shown in the respondent date field when empty (draft `placeholder`). */
  hintPlaceholder: string
  /** Display layout for the date control; instrument-wide in UI (all DATE rows updated together). */
  displayFormat: DateInputDisplayFormat
}

/** Max length for DATE `hintPlaceholder` / draft `placeholder` (Figma 4399:9462). */
export const DATE_HINT_PLACEHOLDER_MAX = 150

export type PictureChoiceImageSize = 'small' | 'medium' | 'large'

export interface PictureChoiceOptionRow {
  id: string
  imageUrl: string
  caption: string
}

export interface PictureChoiceConfig {
  options: PictureChoiceOptionRow[]
  imageSize: PictureChoiceImageSize
}

/** Builder state for `RANKING` (Figma 4399:9425, 4399:9519 — items to rank + image rows). */
export type RankingConfig = PictureChoiceConfig

/** Max characters per “Items to rank” text field (Figma handoff). */
export const RANKING_ITEM_TEXT_MAX = 150

/** Max points on the scale (Figma 4399:9340 — Scale). */
export type RatingScaleMax = 5 | 10

/** Stars vs numeric buttons (Figma 4399:9340 — Display Style). */
export type RatingDisplayStyle = 'stars' | 'numbers'

/** Builder state for `RATING` (Figma 4399:9340). */
export interface RatingConfig {
  scaleMax: RatingScaleMax
  displayStyle: RatingDisplayStyle
  /** Optional respondent-facing label at the low end (draft `ratingLowLabel`). */
  lowLabel: string
  /** Optional respondent-facing label at the high end (draft `ratingHighLabel`). */
  highLabel: string
}

/** Likert agreement scale point count (Figma 4:689 — custom 1–10 via stepper). */
export type LikertPointCount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

export const LIKERT_POINT_COUNT_MIN = 1
export const LIKERT_POINT_COUNT_MAX = 10

/** Max length per scale label in builder (matches campaign draft `likertLabels` item max). */
export const LIKERT_LABEL_MAX = 100

export function isLikertPointCount(n: number): n is LikertPointCount {
  return Number.isInteger(n) && n >= LIKERT_POINT_COUNT_MIN && n <= LIKERT_POINT_COUNT_MAX
}

export function clampLikertPointCount(n: number): LikertPointCount {
  if (!Number.isFinite(n)) return 5
  const rounded = Math.round(n)
  if (rounded < LIKERT_POINT_COUNT_MIN) return LIKERT_POINT_COUNT_MIN
  if (rounded > LIKERT_POINT_COUNT_MAX) return LIKERT_POINT_COUNT_MAX
  return rounded as LikertPointCount
}

/** Default copy for a new Likert question (5-point) and legacy 3 / 7 sizes. */
export function defaultLikertLabels(count: LikertPointCount): string[] {
  switch (count) {
    case 3:
      return ['Disagree', 'Neutral', 'Agree']
    case 5:
      return ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
    case 7:
      return [
        'Strongly Disagree',
        'Disagree',
        'Somewhat Disagree',
        'Neutral',
        'Somewhat Agree',
        'Agree',
        'Strongly Agree',
      ]
    default:
      return Array.from({ length: count }, () => '')
  }
}

/**
 * Resize labels when the Sensemaker changes scale size:
 * reducing drops labels beyond the new count; increasing keeps existing labels
 * and appends empty mandatory slots.
 */
export function likertLabelsForPointCount(
  nextCount: LikertPointCount,
  prevLabels: readonly string[],
  _prevCount?: LikertPointCount,
): string[] {
  return Array.from({ length: nextCount }, (_, i) => (prevLabels[i] ?? '').slice(0, LIKERT_LABEL_MAX))
}

/** Builder state for `LIKERT` (Figma 4:689 — custom scale points, per-point labels, layout). */
export interface LikertConfig {
  pointCount: LikertPointCount
  /** One required label per point (low → high). */
  labels: string[]
  layoutHorizontal: boolean
}

/** Max length for optional slider end labels (Figma 4399:9285 — same order as rating end labels). */
export const SLIDER_END_LABEL_MAX = 100

/** Builder state for `SLIDER` (Figma 4399:9246 — range, step, optional end labels, presets 1–5 / 1–10). */
export interface SliderConfig {
  /** Inclusive lower bound (integer string). */
  minValue: string
  /** Inclusive upper bound (integer string). */
  maxValue: string
  /** Positive integer step (string for controlled input). */
  step: string
  /** Optional label shown at the minimum end of the track. */
  lowLabel: string
  /** Optional label shown at the maximum end of the track. */
  highLabel: string
}

export type QuestionConfig =
  | { type: 'MULTIPLE_CHOICE'; data: MultipleChoiceConfig }
  | { type: 'PICTURE_CHOICE'; data: PictureChoiceConfig }
  | { type: 'RANKING'; data: RankingConfig }
  | { type: 'RATING'; data: RatingConfig }
  | { type: 'LIKERT'; data: LikertConfig }
  | { type: 'SLIDER'; data: SliderConfig }
  | { type: 'YES_NO'; data: YesNoConfig }
  | { type: 'CHECKBOX'; data: CheckboxConfig }
  | { type: 'DROPDOWN'; data: DropdownConfig }
  | { type: 'SHORT_TEXT'; data: ShortTextConfig }
  | { type: 'LONG_TEXT'; data: ShortTextConfig }
  | { type: 'VIDEO'; data: VideoConfig }
  | { type: 'DATE'; data: DateConfig }

/** Short and long free-text questions share `ShortTextConfig` fields in the builder. */
export function isTextAnswerConfig(
  c: QuestionConfig,
): c is { type: 'SHORT_TEXT' | 'LONG_TEXT'; data: ShortTextConfig } {
  return c.type === 'SHORT_TEXT' || c.type === 'LONG_TEXT'
}

export function isVideoConfig(c: QuestionConfig): c is { type: 'VIDEO'; data: VideoConfig } {
  return c.type === 'VIDEO'
}

export type ShortTextLikeConfig =
  | { type: 'SHORT_TEXT'; data: ShortTextConfig }
  | { type: 'LONG_TEXT'; data: ShortTextConfig }
  | { type: 'VIDEO'; data: VideoConfig }

/** Short-text-style min/max/hint — Short Text, Long Text, and Video (MVP). */
export function isShortTextLikeConfig(c: QuestionConfig): c is ShortTextLikeConfig {
  return c.type === 'SHORT_TEXT' || c.type === 'LONG_TEXT' || c.type === 'VIDEO'
}

/** Whole-field bold/italic for prompt/description; composed to markdown on draft save (see `builderQuestionMarkdown.ts`). */
export interface BuilderTextFormatFlags {
  bold: boolean
  italic: boolean
}

export interface BuilderQuestion {
  id: string
  /** Display label in the question list (e.g. "Question 1"). */
  title: string
  type: QuestionTypeId
  /** Plain question copy (no `*` markers in the builder field). */
  questionText: string
  /** Plain description copy (no `*` markers in the builder field). */
  description: string
  /** When set, draft `prompt` is `composeWholeFieldMarkdown(questionText, …)`. */
  questionTextFormat?: BuilderTextFormatFlags
  /** When set, draft `description` is `composeWholeFieldMarkdown(description, …)` when non-empty. */
  descriptionFormat?: BuilderTextFormatFlags
  mandatory: boolean
  /** BFP-1070 — exclude from dashboards/reports; still shown to responders. */
  hidden?: boolean
  config: QuestionConfig
  /** Template-origin rows lock question type until duplicated. */
  fromTemplate?: boolean
  /** BFP-601 — imported from Question Bank (filled bookmark indicator). */
  fromQuestionBank?: boolean
  /** BFP-601 — bank entry id for publish usage tracking. */
  questionBankEntryId?: string
  /** BFP-1143 — answer is written to the responder profile. */
  isProfileUpdate?: boolean
  profileAttributeId?: string
  freshnessTag?: 'PERM' | 'R1M' | 'R3M' | 'R6M' | 'R12M'
  systemKey?: ProfileSystemKey | null
}

function defaultTextAnswerConfig(kind: 'SHORT_TEXT' | 'LONG_TEXT'): QuestionConfig {
  if (kind === 'SHORT_TEXT') {
    return {
      type: 'SHORT_TEXT',
      data: { minLength: '', maxLength: '', hintPlaceholder: '' },
    }
  }
  const data: ShortTextConfig = { minLength: '', maxLength: '', hintPlaceholder: '' }
  return { type: 'LONG_TEXT', data }
}

function defaultMultipleChoiceConfig(): Extract<QuestionConfig, { type: 'MULTIPLE_CHOICE' }> {
  return {
    type: 'MULTIPLE_CHOICE',
    data: {
      options: ['', ''],
      other: false,
      noneOfAbove: false,
      textResponse: null,
      minSelection: '',
      maxSelection: '',
      layoutHorizontal: true,
      multiColumn: false,
      randomize: false,
    },
  }
}

export function defaultConfigFor(type: QuestionTypeId): QuestionConfig {
  switch (type) {
    case 'MULTIPLE_CHOICE':
      return defaultMultipleChoiceConfig()
    case 'YES_NO':
      return {
        type: 'YES_NO',
        data: {
          yesLabel: 'Yes',
          noLabel: 'No',
          layoutHorizontal: true,
          toggleStyle: false,
        },
      }
    case 'CHECKBOX':
      return {
        type: 'CHECKBOX',
        data: {
          options: ['', ''],
          other: false,
          noneOfAbove: false,
          textResponse: null,
          minSelection: '',
          maxSelection: '',
          selectAllLabel: false,
          layoutHorizontal: true,
          multiColumn: false,
          randomize: false,
        },
      }
    case 'DROPDOWN':
      return {
        type: 'DROPDOWN',
        data: {
          options: ['', ''],
          other: false,
          noneOfAbove: false,
          randomize: false,
        },
      }
    case 'PICTURE_CHOICE':
      return {
        type: 'PICTURE_CHOICE',
        data: {
          options: [
            { id: newObjectIdHex(), imageUrl: '', caption: '' },
            { id: newObjectIdHex(), imageUrl: '', caption: '' },
          ],
          imageSize: 'medium',
        },
      }
    case 'SHORT_TEXT':
      return defaultTextAnswerConfig('SHORT_TEXT')
    case 'LONG_TEXT':
      return defaultTextAnswerConfig('LONG_TEXT')
    case 'DATE':
      return {
        type: 'DATE',
        data: {
          earliestSelectableDate: '',
          latestSelectableDate: '',
          disablePastDates: false,
          disableFutureDates: false,
          hintPlaceholder: '',
          displayFormat: 'dd/MM/yyyy',
        },
      }
    case 'RANKING':
      return {
        type: 'RANKING',
        data: {
          options: [
            { id: newObjectIdHex(), imageUrl: '', caption: '' },
            { id: newObjectIdHex(), imageUrl: '', caption: '' },
          ],
          imageSize: 'medium',
        },
      }
    case 'RATING':
      return {
        type: 'RATING',
        data: {
          scaleMax: 5,
          displayStyle: 'stars',
          lowLabel: '',
          highLabel: '',
        },
      }
    case 'LIKERT': {
      const pointCount: LikertPointCount = 5
      return {
        type: 'LIKERT',
        data: {
          pointCount,
          labels: defaultLikertLabels(pointCount),
          layoutHorizontal: false,
        },
      }
    }
    case 'SLIDER':
      return {
        type: 'SLIDER',
        data: {
          minValue: '',
          maxValue: '',
          step: '1',
          lowLabel: '',
          highLabel: '',
        },
      }
    case 'VIDEO':
      return {
        type: 'VIDEO',
        data: {
          sourceKind: 'link',
          videoUrl: '',
          uploadedFileName: '',
          minLength: '',
          maxLength: '',
          hintPlaceholder: '',
        },
      }
    default:
      return defaultMultipleChoiceConfig()
  }
}

export function listSubtitle(type: QuestionTypeId): string {
  switch (type) {
    case 'MULTIPLE_CHOICE':
      return 'Multiple choice'
    case 'YES_NO':
      return 'Yes / No'
    case 'CHECKBOX':
      return 'Checkbox'
    case 'DROPDOWN':
      return 'Dropdown'
    case 'PICTURE_CHOICE':
      return 'Picture choice'
    case 'SHORT_TEXT':
      return 'Short text'
    case 'LONG_TEXT':
      return 'Long text'
    case 'DATE':
      return 'Date'
    case 'RANKING':
      return 'Ranking'
    case 'RATING':
      return 'Rating'
    case 'LIKERT':
      return 'Likert'
    case 'SLIDER':
      return 'Slider'
    case 'VIDEO':
      return 'Video'
    default:
      return 'Question'
  }
}
